import { Link } from "react-router-dom";

const AboutPage = () => {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1682427810373-db240af60d9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxhY291c3RpYyUyMGd1aXRhciUyMG11c2ljaWFuJTIwbW9vZHklMjBkYXJrJTIwYW1iaWFuY2V8ZW58MHx8fHwxNzY5MzI1NDM4fDA&ixlib=rb-4.1.0&q=85"
            alt="Performer"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F0F] via-transparent to-[#0F0F0F]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-6 animate-fade-in">
            The Artist
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#F4F2EE] mb-6 animate-fade-in animate-delay-100" data-testid="about-heading">
            Rooted in Tradition.
            <br />
            <span className="text-[#F4F2EE]/70">Refined for Today.</span>
          </h1>
          <div className="line-gold w-24 mx-auto animate-fade-in animate-delay-200" />
        </div>
      </section>

      {/* Bio Section */}
      <section className="py-24 md:py-32 px-6 lg:px-12" data-testid="bio-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Image */}
            <div className="lg:col-span-2">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1585146720919-9d872c3184c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxhY291c3RpYyUyMGd1aXRhciUyMG11c2ljaWFuJTIwbW9vZHklMjBkYXJrJTIwYW1iaWFuY2V8ZW58MHx8fHwxNzY5MzI1NDM4fDA&ixlib=rb-4.1.0&q=85"
                  alt="Artist portrait"
                  className="w-full aspect-[3/4] object-cover opacity-80"
                />
                <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[#0F0F0F] to-transparent" />
              </div>
            </div>

            {/* Bio Content */}
            <div className="lg:col-span-3 flex flex-col justify-center">
              <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-6">
                Biography
              </p>
              
              <div className="space-y-6 text-[#F4F2EE]/70 leading-relaxed" data-testid="bio-content">
                <p className="text-lg">
                  Steeped in the timeless traditions of American country and soul, this artist brings a distinctive presence to every performance—one defined by restraint, authenticity, and an unwavering commitment to atmosphere.
                </p>
                
                <p>
                  Years of experience performing for discerning audiences have cultivated an intuitive understanding of what elevated gatherings require: not volume, but presence. Not distraction, but enhancement. Each performance is approached as a contribution to the room's character, never its interruption.
                </p>
                
                <p>
                  Known for an ability to read a room and adapt seamlessly, performances are marked by careful attention to pacing, volume, and repertoire. Whether accompanying a cocktail hour's quiet elegance or providing the emotional anchor for a ceremony, the approach remains consistent: serve the moment.
                </p>
                
                <p>
                  This philosophy extends beyond the stage. Professional collaboration with event planners, producers, and venue directors is a cornerstone of every engagement. Clear communication, punctual arrival, and adaptability to changing circumstances are not exceptions—they are expectations.
                </p>
              </div>

              <div className="line-gold-left w-16 my-10" />

              <blockquote className="border-l-2 border-[#B8A369]/30 pl-6" data-testid="about-quote">
                <p className="font-serif text-xl text-[#F4F2EE]/80 italic">
                  "The best performances are felt, not merely heard."
                </p>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 px-6 lg:px-12 bg-[#0A0A0A]" data-testid="values-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-4">
              Philosophy
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#F4F2EE] mb-6">
              Guiding Principles
            </h2>
            <div className="line-gold w-24 mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                title: "Intentional Performance",
                description:
                  "Every song selection, every volume adjustment, every moment is purposeful. Nothing is left to chance in the pursuit of atmosphere.",
              },
              {
                title: "Professional Discretion",
                description:
                  "Reliability and composure define every engagement. The focus remains on enhancing your event, not commanding attention.",
              },
              {
                title: "Timeless Sound",
                description:
                  "Rooted in classic American country and soul, the repertoire spans decades while maintaining a consistent, refined character.",
              },
            ].map((value, index) => (
              <div key={index} className="text-center" data-testid={`value-card-${index}`}>
                <div className="w-px h-12 bg-[#B8A369]/30 mx-auto mb-6" />
                <h3 className="font-serif text-xl text-[#F4F2EE] mb-4">
                  {value.title}
                </h3>
                <p className="text-sm text-[#F4F2EE]/60 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12" data-testid="about-cta">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-6">
            Begin the Conversation
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#F4F2EE] mb-6">
            Let's Discuss Your Event
          </h2>
          <p className="text-[#F4F2EE]/60 leading-relaxed mb-10">
            Every engagement begins with understanding your vision. Share the details of your event, and let's explore how live music can elevate the experience.
          </p>
          <Link
            to="/inquiry"
            className="btn-luxury"
            data-testid="about-cta-button"
          >
            Request Availability
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
